import React, { useEffect } from 'react';
import { ArrowLeft, Scale } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const LegalNotice = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 text-gray-600 hover:text-orange-600"
          >
            <ArrowLeft size={20} />
            Voltar ao Início
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center">
              <Scale className="text-orange-600" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Aviso Legal</h1>
              <p className="text-gray-600 mt-1">Última atualização: {new Date().toLocaleDateString('pt-PT')}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none space-y-8">
            {/* Identificação */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Identificação do Titular</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <div className="space-y-3 text-gray-700">
                  <p><strong className="text-gray-900">Denominação Social:</strong> Somar Distâncias, Lda.</p>
                  <p><strong className="text-gray-900">NIF:</strong> 510210848</p>
                  <p>
                    <strong className="text-gray-900">Sede Social:</strong><br />
                    Estrada de Santa Eulália, Horta Preta<br />
                    Casal das Oliveiras<br />
                    2670-351 Loures, Portugal
                  </p>
                  <p>
                    <strong className="text-gray-900">Email:</strong>{' '}
                    <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline">
                      somardistancias8.geral@gmail.com
                    </a>
                  </p>
                  <p>
                    <strong className="text-gray-900">Telefone:</strong>{' '}
                    <a href="tel:+351961313663" className="text-orange-600 hover:underline">
                      +351 961 313 663
                    </a>
                    {' / '}
                    <a href="tel:+351960120888" className="text-orange-600 hover:underline">
                      +351 960 120 888
                    </a>
                  </p>
                  <p><strong className="text-gray-900">Atividade:</strong> Construção Civil e Obras Públicas</p>
                </div>
              </div>
            </section>

            {/* Objeto */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Objeto do Website</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este website tem como objetivo fornecer informações sobre os serviços de construção civil oferecidos pela 
                Somar Distâncias, Lda., permitindo aos utilizadores:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Conhecer os serviços disponíveis</li>
                <li>Visualizar exemplos de projetos realizados</li>
                <li>Solicitar orçamentos e informações</li>
                <li>Entrar em contacto com a empresa</li>
              </ul>
            </section>

            {/* Propriedade Intelectual */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Propriedade Intelectual</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Todos os direitos de propriedade intelectual sobre este website e o seu conteúdo (incluindo, mas não limitado a, 
                textos, gráficos, logotipos, ícones, imagens, fotografias, vídeos, sons e software) são propriedade da 
                Somar Distâncias, Lda. ou dos seus licenciadores.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Todos os direitos estão reservados. A reprodução, distribuição, transmissão, modificação ou publicação de 
                qualquer conteúdo deste website sem autorização prévia por escrito é estritamente proibida.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>⚠️ Aviso:</strong> A utilização não autorizada de conteúdo protegido por direitos de autor pode 
                  constituir violação das leis de direitos de autor e estar sujeita a sanções civis e criminais.
                </p>
              </div>
            </section>

            {/* Responsabilidade */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Limitação de Responsabilidade</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1. Conteúdo do Website</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                A Somar Distâncias, Lda. esforça-se para garantir que as informações fornecidas neste website sejam precisas 
                e atualizadas. No entanto, não podemos garantir a exatidão, completude ou atualidade das informações disponibilizadas.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                As informações fornecidas destinam-se apenas a fins informativos gerais e não constituem aconselhamento 
                técnico ou profissional específico.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2. Disponibilidade do Website</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                A Somar Distâncias, Lda. não garante que o acesso ao website será ininterrupto ou isento de erros. O website 
                pode estar temporariamente indisponível devido a manutenção, atualizações ou falhas técnicas.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3. Links Externos</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este website pode conter links para websites de terceiros. A Somar Distâncias, Lda. não é responsável pelo 
                conteúdo, políticas de privacidade ou práticas de websites de terceiros. A inclusão de links não implica 
                qualquer endosso ou aprovação por parte da Somar Distâncias, Lda.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.4. Vírus e Segurança</h3>
              <p className="text-gray-700 leading-relaxed">
                Embora implementemos medidas de segurança razoáveis, não podemos garantir que o website esteja livre de 
                vírus ou outros componentes prejudiciais. É da responsabilidade do utilizador garantir que o seu equipamento 
                possui software de proteção adequado.
              </p>
            </section>

            {/* Proteção de Dados */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Proteção de Dados Pessoais</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A Somar Distâncias, Lda. compromete-se a proteger a privacidade e os dados pessoais dos utilizadores em 
                conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD - Regulamento UE 2016/679) e a 
                legislação portuguesa aplicável.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para informações detalhadas sobre como recolhemos, utilizamos e protegemos os seus dados pessoais, 
                consulte a nossa{' '}
                <a href="/politica-privacidade" className="text-orange-600 hover:underline font-medium">
                  Política de Privacidade
                </a>.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Direitos dos Titulares de Dados</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Em conformidade com o RGPD, os utilizadores têm direito a:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  <li>Acesso aos seus dados pessoais</li>
                  <li>Retificação de dados incorretos</li>
                  <li>Apagamento dos dados ("direito ao esquecimento")</li>
                  <li>Limitação do tratamento</li>
                  <li>Portabilidade dos dados</li>
                  <li>Oposição ao tratamento</li>
                </ul>
                <p className="text-sm text-gray-700 mt-3">
                  Para exercer estes direitos, contacte:{' '}
                  <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline">
                    somardistancias8.geral@gmail.com
                  </a>
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Utilização de Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este website utiliza cookies para melhorar a experiência do utilizador e analisar o tráfego do website. 
                Os cookies são pequenos ficheiros de texto armazenados no seu dispositivo quando visita o nosso website.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para informações detalhadas sobre os tipos de cookies que utilizamos, as suas finalidades e como pode 
                gerir as suas preferências, consulte a nossa{' '}
                <a href="/politica-cookies" className="text-orange-600 hover:underline font-medium">
                  Política de Cookies
                </a>.
              </p>
            </section>

            {/* Lei Aplicável */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Lei Aplicável e Resolução de Conflitos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este Aviso Legal é regido pela legislação portuguesa. Qualquer litígio emergente ou relacionado com a 
                utilização deste website será da competência exclusiva dos tribunais da comarca de Loures, Portugal.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.1. Resolução Alternativa de Litígios</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Em conformidade com a Lei n.º 144/2015, de 8 de setembro, informamos que, em caso de litígio, o consumidor 
                pode recorrer a uma Entidade de Resolução Alternativa de Litígios de consumo:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Centro de Arbitragem de Conflitos de Consumo de Lisboa</strong>
                </p>
                <p className="text-sm text-gray-700">
                  Website:{' '}
                  <a href="http://www.centroarbitragemlisboa.pt" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                    www.centroarbitragemlisboa.pt
                  </a>
                </p>
                <p className="text-sm text-gray-700 mt-3 mb-2">
                  <strong>Portal do Consumidor</strong>
                </p>
                <p className="text-sm text-gray-700">
                  Website:{' '}
                  <a href="https://www.consumidor.gov.pt" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                    www.consumidor.gov.pt
                  </a>
                </p>
              </div>
            </section>

            {/* Livro de Reclamações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Livro de Reclamações</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Em cumprimento do disposto no Decreto-Lei n.º 156/2005, de 15 de setembro, alterado pelo Decreto-Lei n.º 371/2007, 
                de 6 de novembro, a Somar Distâncias, Lda. dispõe de Livro de Reclamações.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Os consumidores podem também apresentar as suas reclamações através da plataforma online do Portal da Queixa:{' '}
                <a href="https://www.portaldaqueixa.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                  www.portaldaqueixa.com
                </a>
              </p>
            </section>

            {/* Alterações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Alterações ao Aviso Legal</h2>
              <p className="text-gray-700 leading-relaxed">
                A Somar Distâncias, Lda. reserva-se o direito de alterar este Aviso Legal a qualquer momento. As alterações 
                entrarão em vigor imediatamente após a sua publicação neste website. A data da última atualização é indicada 
                no início deste documento.
              </p>
            </section>

            {/* Contacto */}
            <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contactos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para qualquer questão, dúvida ou esclarecimento relacionado com este Aviso Legal ou com a utilização do website, 
                pode contactar-nos através de:
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-gray-700">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline">
                    somardistancias8.geral@gmail.com
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>Telefone:</strong>{' '}
                  <a href="tel:+351961313663" className="text-orange-600 hover:underline">
                    +351 961 313 663
                  </a>
                  {' / '}
                  <a href="tel:+351960120888" className="text-orange-600 hover:underline">
                    +351 960 120 888
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>Morada:</strong><br />
                  Estrada de Santa Eulália, Horta Preta, Casal das Oliveiras<br />
                  2670-351 Loures, Portugal
                </p>
                <p className="text-gray-700">
                  <strong>Horário de Atendimento:</strong> Segunda a Sexta, 09:00 - 17:00
                </p>
              </div>
            </section>
          </div>

          {/* Back Button */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <Button
              onClick={() => navigate('/')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalNotice;
